import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Clock, Phone, CheckCircle, ArrowRight } from 'lucide-react';

// Données des zones de livraison
const zones = {
  prioritaire: {
    title: "Zone Prioritaire",
    subtitle: "Livraison rapide sous 24-48h",
    color: "green",
    cities: [
      { name: "Le Havre", population: "170 000 hab.", distance: "15 min", slug: "le-havre" },
      { name: "Étretat", population: "1 200 hab.", distance: "15 min", slug: "etretat" },
      { name: "Fécamp", population: "18 000 hab.", distance: "25 min", slug: "fecamp" },
      { name: "Montivilliers", population: "16 000 hab.", distance: "20 min", slug: "montivilliers" },
      { name: "Harfleur", population: "8 000 hab.", distance: "18 min", slug: "harfleur" },
      { name: "Gonfreville-l'Orcher", population: "9 000 hab.", distance: "20 min" },
      { name: "Sainte-Adresse", population: "7 500 hab.", distance: "18 min", slug: "sainte-adresse" },
      { name: "Octeville-sur-Mer", population: "5 000 hab.", distance: "10 min" },
      { name: "Cauville-sur-Mer", population: "1 500 hab.", distance: "Base 3DLoc", isBase: true },
    ]
  },
  secondaire: {
    title: "Zone Étendue",
    subtitle: "Livraison sous 48-72h",
    color: "blue",
    cities: [
      { name: "Rouen", population: "115 000 hab.", distance: "1h", slug: "rouen" },
      { name: "Dieppe", population: "28 000 hab.", distance: "1h15", slug: "dieppe" },
      { name: "Honfleur", population: "6 500 hab.", distance: "35 min", slug: "honfleur" },
      { name: "Deauville", population: "3 500 hab.", distance: "45 min", slug: "deauville" },
      { name: "Trouville-sur-Mer", population: "4 500 hab.", distance: "45 min" },
      { name: "Caen", population: "106 000 hab.", distance: "1h15", slug: "caen" },
      { name: "Bolbec", population: "11 000 hab.", distance: "30 min", slug: "bolbec" },
      { name: "Lillebonne", population: "9 000 hab.", distance: "35 min", slug: "lillebonne" },
      { name: "Pont-Audemer", population: "8 500 hab.", distance: "40 min", slug: "pont-audemer" },
      { name: "Lisieux", population: "20 000 hab.", distance: "1h", slug: "lisieux" },
      { name: "Évreux", population: "47 000 hab.", distance: "1h30", slug: "evreux" },
    ]
  },
  sectors: {
    title: "Zones & Secteurs",
    subtitle: "Découvrez toutes les communes par zone géographique",
    color: "orange",
    items: [
      { name: "Pays de Caux", communes: "18 communes", icon: "🌾", slug: "pays-de-caux" },
      { name: "Agglomération du Havre", communes: "24 communes", icon: "🏙️", slug: "agglo-le-havre" },
      { name: "Vallée de Seine & Estuaire", communes: "21 communes", icon: "🌉", slug: "vallee-de-seine" },
      { name: "Côte Fleurie & Pays d'Auge", communes: "12 communes", icon: "🌊", slug: "cote-fleurie" }
    ]
  }
};

const departements = [
  { name: "Seine-Maritime (76)", description: "Couverture complète du département" },
  { name: "Calvados (14)", description: "Honfleur, Deauville, Lisieux et environs" },
  { name: "Eure (27)", description: "Sur demande" },
];

const ZonesLivraisonPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-black mb-6">
              Nos Zones de Livraison en Normandie
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              3DLoc livre votre matériel dans toute la <strong>Haute et Basse-Normandie</strong>. 
              Du Havre à Rouen, d'Étretat à Honfleur, nous couvrons la Seine-Maritime et le Calvados.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <MapPin size={20} />
                <span className="font-semibold">+20 villes desservies</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Truck size={20} />
                <span className="font-semibold">Livraison 7J/7</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Clock size={20} />
                <span className="font-semibold">Sur rendez-vous</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Notre Zone d'Intervention</h2>
            <p className="text-slate-600">Depuis notre dépôt à Cauville-sur-Mer, nous intervenons dans toute la Normandie</p>
          </div>
          
          {/* Carte interactive iframe OpenStreetMap */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl mb-8">
            <iframe 
              title="Zone de livraison 3DLoc en Normandie"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-0.5%2C49.2%2C1.2%2C49.9&layer=mapnik&marker=49.5833%2C0.1167"
              width="100%" 
              height="450" 
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
            ></iframe>
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium">Dépôt 3DLoc - Cauville-sur-Mer</span>
              </div>
            </div>
          </div>

          {/* Légende */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <div>
                <p className="font-semibold text-green-800">Zone Prioritaire (0-30 km)</p>
                <p className="text-sm text-green-600">Livraison rapide sous 24-48h</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <div>
                <p className="font-semibold text-blue-800">Zone Étendue (30-80 km)</p>
                <p className="text-sm text-blue-600">Livraison sous 48-72h</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Zones & Secteurs - Tier 2 SEO */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-6 h-6 bg-orange-500 rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{zones.sectors.title}</h2>
                <p className="text-orange-600">{zones.sectors.subtitle}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="zones-sectors-grid">
              {zones.sectors.items.map((sector) => (
                <Link
                  key={sector.slug}
                  to={`/location-materiel-zone/${sector.slug}`}
                  className="group flex flex-col p-6 bg-white rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-lg transition-all"
                  data-testid={`zone-sector-${sector.slug}`}
                >
                  <span className="text-4xl mb-3">{sector.icon}</span>
                  <h3 className="font-bold text-lg text-slate-800 mb-1 group-hover:text-orange-600">
                    {sector.name}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3">{sector.communes}</p>
                  <span className="mt-auto inline-flex items-center gap-1 text-orange-600 font-medium text-sm">
                    Découvrir <ArrowRight size={16} />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Zone Prioritaire */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-6 h-6 bg-green-500 rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{zones.prioritaire.title}</h2>
                <p className="text-green-600">{zones.prioritaire.subtitle}</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {zones.prioritaire.cities.map((city) => {
                const CityWrapper = city.slug ? Link : 'div';
                const wrapperProps = city.slug ? { to: `/location-materiel/${city.slug}` } : {};
                
                return (
                  <CityWrapper
                    key={city.name}
                    {...wrapperProps}
                    className={`block p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
                      city.isBase 
                        ? 'bg-green-500 text-white border-green-500' 
                        : 'bg-white border-green-200 hover:border-green-400'
                    } ${city.slug ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className={`font-bold text-lg ${city.isBase ? 'text-white' : 'text-slate-800'}`}>
                          {city.name}
                          {city.slug && <ArrowRight size={16} className="inline ml-1" />}
                        </h3>
                        <p className={`text-sm ${city.isBase ? 'text-green-100' : 'text-slate-500'}`}>
                          {city.population}
                        </p>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                        city.isBase 
                          ? 'bg-white/20 text-white' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        <MapPin size={14} />
                        {city.distance}
                      </div>
                    </div>
                    {city.isBase && (
                      <div className="mt-2 flex items-center gap-1 text-sm">
                        <CheckCircle size={16} />
                        <span>Notre dépôt principal</span>
                      </div>
                    )}
                  </CityWrapper>
                );
              })}
            </div>
          </div>

          {/* Zone Secondaire */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{zones.secondaire.title}</h2>
                <p className="text-blue-600">{zones.secondaire.subtitle}</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {zones.secondaire.cities.map((city) => {
                const CityWrapper = city.slug ? Link : 'div';
                const wrapperProps = city.slug ? { to: `/location-materiel/${city.slug}` } : {};
                
                return (
                  <CityWrapper
                    key={city.name}
                    {...wrapperProps}
                    className={`block p-4 bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg ${city.slug ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg text-slate-800">
                          {city.name}
                          {city.slug && <ArrowRight size={16} className="inline ml-1" />}
                        </h3>
                        <p className="text-sm text-slate-500">{city.population}</p>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                        <MapPin size={14} />
                        {city.distance}
                      </div>
                    </div>
                  </CityWrapper>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Départements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">Départements Couverts</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {departements.map((dept) => (
              <div key={dept.name} className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <h3 className="text-xl font-bold text-slate-800 mb-2">{dept.name}</h3>
                <p className="text-slate-600">{dept.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Votre ville n'est pas listée ?</h2>
          <p className="text-xl text-green-100 mb-8">
            Contactez-nous pour vérifier si nous pouvons vous livrer. 
            Nous étudions chaque demande avec attention.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:+33623893298"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
            >
              <Phone size={20} />
              06 23 89 32 98
            </a>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl border-2 border-white hover:bg-green-400 transition-colors"
            >
              Demander un devis
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              Location de Matériel avec Livraison en Normandie
            </h2>
            <p className="text-slate-600 mb-4">
              <strong>3DLoc</strong> est votre partenaire de confiance pour la <strong>location de matériel en Normandie</strong>. 
              Basés à <strong>Cauville-sur-Mer</strong>, à seulement 15 minutes du <strong>Havre</strong> et d'<strong>Étretat</strong>, 
              nous livrons dans toute la <strong>Seine-Maritime</strong> et le <strong>Calvados</strong>.
            </p>
            <p className="text-slate-600 mb-4">
              Que vous organisiez un <strong>mariage à Honfleur</strong>, un <strong>événement d'entreprise à Rouen</strong>, 
              ou que vous ayez besoin d'<strong>outillage pour vos travaux à Fécamp</strong>, notre équipe vous accompagne 
              avec un service de livraison fiable et ponctuel.
            </p>
            <p className="text-slate-600">
              Notre flotte de véhicules nous permet d'acheminer rapidement <strong>chapiteaux, structures gonflables, 
              mobilier événementiel, matériel de bricolage et de jardinage</strong> partout en Normandie. 
              Tarifs de livraison sur devis, adaptés à la distance et au volume de matériel.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default ZonesLivraisonPage;
